import {
  SentTreatmentPayment,
  TextedPaymentLink,
  TimelinePaymentOrder,
  TreatmentPaymentOrderPaymentIntent,
} from '@/components/Timeline/types';
import { getConfig } from '@/config';
import { useCherryStatus } from '@/hooks/useCherryStatus';
import { useTreatmentPaymentCatalog } from '@/hooks/useTreatmentPaymentCatalog';
import { ActorAvatar } from '@/components/Timeline/TimelineItems/ActorAvatar';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { alert } from '@/services/alert';
import { formatDatetime } from '@/services/date';
import { formatDollars } from '@/services/money';
import {
  canSendPaymentLink,
  cherrySendBlock,
  describeCherryStatus,
  describeMethodState,
  describePaymentError,
  formatProcessor,
  isLiveLeg,
  methodActions,
  nextMethodAmount,
  oldestUnpaidLegAge,
  paymentLinkForLeg,
  SENDABLE_PROCESSORS,
  sumAllocated,
} from '@/services/payments';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  Menu,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCashBanknote,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCircleX,
  IconClock,
  IconDots,
  IconLink,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import { ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';

/**
 * The only two status colours the timeline allows. A third would buy urgency the
 * label already supplies.
 */

const DAY_MS = 86_400_000;

/**
 * What picking a rail actually does, because the name alone does not say it.
 * Stripe mints a link the CI sends; Cherry runs a financing application and
 * texts the patient itself.
 */
const RAIL_HINTS: Record<string, string> = {
  STRIPE: 'payment link',
  CHERRY: 'financing link',
};

/**
 * How a Cherry status is coloured. `caveat` is deliberately not red: the send is
 * still allowed, and painting an unanswered lookup as an error sends reps
 * chasing applications that already exist.
 */
const CHERRY_TONE_COLORS: Record<'ok' | 'blocked' | 'caveat', string> = {
  ok: 'teal',
  blocked: 'red',
  caveat: 'dimmed',
};

function ageInDays(from: string, now: Date): number {
  return Math.floor((now.getTime() - Date.parse(from)) / DAY_MS);
}

/**
 * Surface 01 — the payment order card.
 *
 * Collapsed: total due, paid so far, balance left, and the order's age. Expanded:
 * the Order section beside Payment methods — one row per leg, the `+ Add payment
 * method` picker, and the running allocation against the order total.
 *
 * This card **aggregates, it does not record**. Every figure derives from the
 * order or the ledger, so nothing here is true only here — which is what lets the
 * event cards stay the audit trail.
 *
 * Built on `TimelineCard` with a gutter marker rather than its own `Paper` with a
 * teal left stripe (BAR-2525). The stripe and the header chevron were this card's
 * alone; the marker in the timeline gutter is what carries the Teal billing hue
 * for every other surface, and the expansion control is the centre-on-line chip
 * the eligibility card already uses.
 *
 * Sending the link lives here rather than in the create drawer: the drawer closes
 * on create, and sending acts on an order that already exists. `onSendPaymentLink`
 * is absent for a viewer who cannot take payments, so the control disappears
 * rather than failing on click.
 *
 * Adding a method *is* sending its link — `POST /send-link` mints the
 * PaymentIntent, so there is no draft leg to save and no separate Save. That is
 * why the picker sits in the Payment methods section instead of a form above it:
 * the row a rep is creating and the rows they are reading are the same list.
 */
export function PaymentOrderCard({ item, now = new Date() }: { item: TimelinePaymentOrder; now?: Date }) {
  const [expanded, setExpanded] = useState(false);
  const [sent, setSent] = useState<SentTreatmentPayment | undefined>();
  const [sending, setSending] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [processor, setProcessor] = useState<string>('STRIPE');
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | string>('');
  const [alsoText, setAlsoText] = useState(false);
  const [texted, setTexted] = useState<TextedPaymentLink | undefined>();
  const { onSendPaymentLink, onRecordPayment, onTextPaymentLink, textablePhone, onCherryStatus } = useTimelineContext();
  const { practices } = useTreatmentPaymentCatalog(choosing);
  const { order, amountRefunded, ledgerTruncated, ledgerPartial, legSettledAt, createdBy } = item;

  const settled = order.balance <= 0;
  const age = ageInDays(order.createdAt, now);
  const serviceName = order.items.find((line) => line.serviceName)?.serviceName;
  const waitingDays = oldestUnpaidLegAge(order, now);

  // Hidden, not disabled, on a settled balance: sendTreatmentPaymentLink throws
  // on a non-positive balance, so the action cannot succeed.
  const canSend = !!onSendPaymentLink && canSendPaymentLink(order);

  // Cherry books against a specific practice's ledger and has no safe default —
  // the Close path's silent fallback is what BAR-2526 refuses to repeat — so the
  // send is blocked until one is chosen.
  const needsPractice = processor === 'CHERRY';

  // Cherry approves patients in a phase we do not automate, so the rail can be
  // offered against a patient Cherry has never heard of. Asked only once the
  // Cherry draft is open, because Cherry rate-limits the lookup (BAR-2604).
  const { status: cherryStatus, loading: cherryChecking } = useCherryStatus(choosing && needsPractice, onCherryStatus);
  const cherryBlock = needsPractice ? cherrySendBlock(cherryStatus) : undefined;
  const cherryNote = needsPractice ? describeCherryStatus(cherryStatus) : undefined;

  // An untouched field sends nothing, so the backend derives the balance itself
  // rather than trusting a figure this card rounded to two places. Only a
  // deliberately edited amount travels.
  const typedAmount = typeof amount === 'number' ? amount : Number.parseFloat(amount);
  const partialAmount = Number.isFinite(typedAmount) && typedAmount !== order.balance ? typedAmount : undefined;

  // The backend refuses these too — it is the only real guard, since nothing in
  // payment-backend compares the amount to the order. Checking here as well
  // saves a round trip to discover a typo.
  const amountIsSendable = partialAmount === undefined || (partialAmount > 0 && partialAmount <= order.balance);

  // `cherryChecking` is included so a fast click cannot beat the verdict: the
  // whole point is that the send never leaves before we know Cherry can take it.
  const readyToSend = (!needsPractice || !!practiceId) && amountIsSendable && !cherryBlock && !cherryChecking;

  // The draft counts toward the allocation the moment it exists, which is what
  // makes the line move as the amount is typed rather than only after a send.
  const draftAmount = choosing && Number.isFinite(typedAmount) ? typedAmount : 0;
  const allocated = sumAllocated(order.paymentIntents) + draftAmount;
  const fullyAllocated = Math.abs(allocated - order.amountTotal) < 0.005;
  const methodCount = order.paymentIntents.length + (choosing ? 1 : 0);

  /**
   * The opt-in is offered on the Stripe rail only — Cherry texts the patient
   * itself, so a second text would send two messages for one request.
   *
   * Shown even when it cannot be used, unlike before. The control used to require
   * `onTextPaymentLink`, which is absent whenever the patient has no usable mobile
   * number, so on those patients it silently was not there and a rep had no way to
   * tell whether the feature was missing or the number was. It now renders
   * disabled with the reason.
   */
  const offerAlsoText = processor === 'STRIPE';
  // One gate, the callback — the page withholds it exactly when there is no usable
  // number. `textablePhone` is for the label only, so the two cannot disagree
  // about whether texting is possible.
  const canAlsoText = offerAlsoText && !!onTextPaymentLink;

  /**
   * What the opt-in says under its label: the number it would text, or why it
   * cannot. Driven by `textablePhone` rather than by `canAlsoText`, so it never
   * claims a number it does not have.
   */
  let alsoTextNote: string | undefined;
  if (textablePhone) {
    alsoTextNote = `to ···${textablePhone.slice(-4)}`;
  } else if (!canAlsoText) {
    alsoTextNote = 'No mobile number on file';
  }

  // Picking the rail is what creates the draft, so everything rail-specific
  // resets with it: a practice means nothing to Stripe, and a Stripe link left
  // on screen over a Cherry form is a live wrong link.
  function startDraft(rail: string): void {
    setProcessor(rail);
    setPracticeId(null);
    setSent(undefined);
    setTexted(undefined);
    setAlsoText(false);
    setAmount(nextMethodAmount(order));
    setChoosing(true);
    setPicking(false);
  }

  function discardDraft(): void {
    setChoosing(false);
    setPracticeId(null);
  }

  async function sendLink() {
    if (!onSendPaymentLink || !readyToSend) {
      return;
    }

    setSending(true);

    try {
      const payment = await onSendPaymentLink({
        paymentProcessor: processor,
        practiceId: needsPractice ? (practiceId ?? undefined) : undefined,
        amount: partialAmount,
      });

      setSent(payment);
      setChoosing(false);

      if (payment.processor === 'CHERRY') {
        alert.success(`Cherry texted the request to the number ending ${payment.requestedToLast4}.`);
        return;
      }

      if (!alsoText || !onTextPaymentLink) {
        alert.success('The payment link has been sent.');
        return;
      }

      // Two independent side effects, and the email is already gone by the time
      // this runs. A failed text must therefore not read as a failed send: a CI
      // who re-sends on that message emails the patient a second time. Report
      // the partial outcome and leave Copy link available so they can deliver
      // it themselves.
      try {
        const text = await onTextPaymentLink(payment.paymentLink);
        setTexted(text);
        alert.success(`Emailed the link and texted it to ···${text.toLast4}.`);
      } catch (e) {
        alert.error(
          `Emailed the link, but the text did not go through: ${e instanceof Error ? e.message : 'unknown error'}`
        );
      }
    } catch (e) {
      alert.error(describePaymentError(e, 'Could not send the payment request'));
    } finally {
      setSending(false);
    }
  }

  // Re-delivers a link that already exists. `POST /send-link` mints a fresh
  // PaymentIntent on every call and nothing cancels the old one, so a "resend"
  // routed through the send path leaves two payable links against one balance.
  async function copyLink(link: string): Promise<void> {
    await navigator.clipboard.writeText(link);
    alert.success('Payment link copied.');
  }

  async function resendLink(link: string): Promise<void> {
    if (!onTextPaymentLink) {
      return;
    }

    try {
      const text = await onTextPaymentLink(link);
      setTexted(text);
      alert.success(`Texted the same link to ···${text.toLast4}.`);
    } catch (e) {
      alert.error(`The text did not go through: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  // A declined or cancelled leg cannot be paid, so minting its replacement is the
  // one add that carries no double-payment risk. Pre-filled with what the dead
  // leg was holding rather than the whole balance.
  function replaceLeg(dead: TreatmentPaymentOrderPaymentIntent): void {
    setProcessor(dead.processor);
    setPracticeId(null);
    setSent(undefined);
    setTexted(undefined);
    setAlsoText(false);
    setAmount(Math.min(dead.amount, order.balance));
    setChoosing(true);
    setPicking(false);
  }

  // The header icon acts on the most recently issued live leg — the link a rep
  // means when they say "the" link. Absent when no leg is payable, rather than
  // copying a dead URL.
  const newestLiveLink = [...order.paymentIntents]
    .filter(isLiveLeg)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((leg) => paymentLinkForLeg(leg, getConfig().paymentFrontendUrl))
    .find((link) => !!link);

  // Falls back to the link this session just minted. Without a configured
  // payment-frontend origin no leg resolves to a URL, and a rep who had just
  // sent a link would otherwise lose the ability to copy it — a silent
  // capability loss on a missing env var.
  const headerLink = newestLiveLink ?? (sent?.processor === 'STRIPE' ? sent.paymentLink : undefined);

  return (
    <TimelineCard
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text size="md" fw={700}>
            {serviceName ? `Payment order initiated for ${serviceName}` : 'Payment order'}
          </Text>
          {/* The fixed meta order every billing card shares: copy link, 3-dot,
              avatar, date. */}
          <Group gap="xs" align="center" wrap="nowrap">
            {headerLink && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Copy payment link"
                onClick={() => void copyLink(headerLink)}
              >
                <IconLink size={15} />
              </ActionIcon>
            )}
            {(canSend || !!onRecordPayment) && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Order actions">
                    <IconDots size={15} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {canSend && (
                    <Menu.Item
                      leftSection={<IconPlus size={14} />}
                      onClick={() => {
                        setExpanded(true);
                        setPicking(true);
                      }}
                    >
                      Add payment method
                    </Menu.Item>
                  )}
                  {/* Financed rails have no settlement webhook, so a
                      Cherry-portal or CareCredit payment only reaches the ledger
                      if someone records it. */}
                  {onRecordPayment && canSendPaymentLink(order) && (
                    <Menu.Item leftSection={<IconCashBanknote size={14} />} onClick={onRecordPayment}>
                      Record a payment
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            {/* No creator is recorded on a treatment order today, so the avatar
                says "a teammate, in Orbit" and stops short of naming one. */}
            <ActorAvatar variant="teammate" label="Created in Orbit" />
            <Text variant="timestamp">{age === 0 ? 'today' : `${age} days`}</Text>
          </Group>
        </Group>
      }
    >
      <Stack gap="xs">
        <Group gap="xl">
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              Total due
            </Text>
            <Text>{formatDollars(order.amountTotal)}</Text>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              Paid so far
            </Text>
            <Text>{formatDollars(order.amountPaid)}</Text>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              Balance left
            </Text>
            {/* The age of the oldest unpaid leg rides with the balance, so a
                stalling order is visible without expanding anything. The order's
                own age cannot do this job — a months-old order whose link went
                out yesterday is not stalling. */}
            <Group gap={4} align="center" wrap="nowrap">
              {settled ? (
                <IconCheck size={14} color={ORBIT_SETTLED} />
              ) : (
                <IconClock size={14} color={ORBIT_NEEDS_ACTION} />
              )}
              <Text c={settled ? ORBIT_SETTLED : ORBIT_NEEDS_ACTION}>
                {formatDollars(order.balance)}
                {waitingDays !== undefined && ` · ${waitingDays === 0 ? 'today' : `${waitingDays} days`}`}
              </Text>
            </Group>
          </Stack>
          {amountRefunded > 0 && (
            <Stack gap={0}>
              <Text size="xs" c="dimmed">
                Refunded
              </Text>
              <Text>{formatDollars(amountRefunded)}</Text>
            </Stack>
          )}
        </Group>

        {/* Only the text gets a time. The email is sent by payment-backend,
            which persists nothing and reports nothing about it, so a timestamp
            here would be invented. The SMS is ours, so its time is real — and
            it is also on the timeline as an ordinary SMS card. */}
        {texted && (
          <Text size="xs" c="dimmed">
            Texted to ···{texted.toLast4} · {formatDatetime(texted.sentAt)}
          </Text>
        )}

        {/* A card showing dollar figures has to be able to say its history is
            incomplete. The two flags need different follow-up: one is a known
            cap, the other is money we could not read at all. */}
        {ledgerPartial && (
          <Alert color="red" title="Payment history incomplete">
            At least one payment method could not be read, so money that has moved may be missing from this card.
          </Alert>
        )}
        {ledgerTruncated && !ledgerPartial && (
          <Alert color="yellow" title="Payment history truncated">
            This order has more charges than are shown.
          </Alert>
        )}

        {/* Mounted only when open, not merely hidden: a long timeline would
            otherwise build every order's line items and payment rows for cards
            nobody has expanded. */}
        {expanded && (
          <Stack gap="sm" pt="xs">
            <Divider />

            {/* Order beside Payment methods, hairline rows, no card inside a
                card. */}
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              <Stack gap={6}>
                <Text size="xs" fw={600} c="dimmed">
                  Order
                </Text>
                {/* Location, target procedure date and payer belong here in the
                    design and are absent on purpose: no column for any of them
                    exists on `CheckoutSession` or `PaymentIntent`. Inventing a
                    row would be worse than omitting it. */}
                {order.items.map((line, index) => (
                  <Group key={`${line.serviceName ?? 'unnamed'}-${index}`} gap="sm" wrap="nowrap" align="flex-start">
                    <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                      Service
                    </Text>
                    <Text size="sm">{line.serviceName ?? 'Unnamed service'}</Text>
                  </Group>
                ))}
                {/* A line-item discount is the common case — the backend puts
                    the caller's discount on the line, not on the order — and the
                    order total is already net of it. Without this row the money
                    simply vanishes: the card would show $12,495 for a $12,995
                    service and never say why. */}
                {order.items
                  .filter((line) => line.amountDiscount > 0)
                  .map((line, index) => (
                    <Group key={`discount-${index}`} gap="sm" wrap="nowrap" align="flex-start">
                      <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                        Discount
                      </Text>
                      <Text size="sm">
                        {line.discountName ?? 'Discount'} · −{formatDollars(line.amountDiscount)}
                      </Text>
                    </Group>
                  ))}
                {order.discount && (
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                      Discount
                    </Text>
                    <Text size="sm">
                      {order.discount.name} · −{formatDollars(order.discount.amount)}
                    </Text>
                  </Group>
                )}
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                    Created by
                  </Text>
                  {/* Rendered even when unknown. This row is what a commission
                      payout is read from (BAR-2614), so "we do not know" has to
                      be visible — omitting the row would let an unattributed
                      sale look the same as one nobody has looked at yet. */}
                  <Text size="sm" c={createdBy ? undefined : 'dimmed'}>
                    {createdBy ?? 'Unattributed'}
                  </Text>
                </Group>
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>
                    Order number
                  </Text>
                  <Text size="sm" style={{ wordBreak: 'break-all' }}>
                    {order.checkoutSessionId}
                  </Text>
                </Group>
              </Stack>

              <Stack gap={6}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text size="xs" fw={600} c="dimmed">
                    Payment methods
                  </Text>
                  {canSend && (
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      leftSection={<IconPlus size={13} />}
                      onClick={() => setPicking((open) => !open)}
                    >
                      Add
                    </Button>
                  )}
                </Group>

                {order.paymentIntents.length === 0 && !choosing && (
                  <Text size="sm" c="dimmed">
                    No payment method issued yet.
                  </Text>
                )}

                {order.paymentIntents.map((leg) => (
                  <MethodRow
                    key={leg.id}
                    leg={leg}
                    now={now}
                    settledAt={legSettledAt[leg.id]}
                    link={paymentLinkForLeg(leg, getConfig().paymentFrontendUrl)}
                    canResend={!!onTextPaymentLink}
                    canReplace={canSend}
                    onCopy={copyLink}
                    onResend={resendLink}
                    onReplace={replaceLeg}
                  />
                ))}

                {/* Inline rather than a Menu or Popover: the rails are two short
                    labels, a portal buys nothing at this size, and
                    Popover.Target swallows the child's onClick. */}
                {canSend && picking && (
                  <Group gap="xs" pt={2}>
                    {SENDABLE_PROCESSORS.map((rail) => (
                      <Button key={rail.value} variant="default" size="xs" onClick={() => startDraft(rail.value)}>
                        {rail.label}
                        {RAIL_HINTS[rail.value] && (
                          <Text component="span" size="xs" c="dimmed" fw={400} ml={6}>
                            {RAIL_HINTS[rail.value]}
                          </Text>
                        )}
                      </Button>
                    ))}
                    <Button variant="subtle" color="gray" size="xs" onClick={() => setPicking(false)}>
                      Cancel
                    </Button>
                  </Group>
                )}

                {/* The draft row. An issued leg has no × and no editable amount:
                    payment-backend's cancel is not exposed to Orbit's bot, so
                    both controls could only fail. A draft is discarded with its
                    own × because that costs nothing — it has not been sent. */}
                {canSend && choosing && (
                  <Stack gap={4} pt={2}>
                    <Group gap="xs" align="flex-end" wrap="wrap">
                      <Text size="sm" w={70} pb={6}>
                        {formatProcessor(processor) ?? processor}
                      </Text>
                      <NumberInput
                        label="Amount"
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator
                        min={0}
                        max={order.balance}
                        value={amount}
                        onChange={setAmount}
                        size="xs"
                        w={130}
                        error={amountIsSendable ? undefined : 'More than the balance'}
                      />
                      {needsPractice && (
                        <Select
                          label="Cherry practice"
                          placeholder="Select a practice"
                          data={practices.map((practice) => ({ value: practice.id, label: practice.name }))}
                          value={practiceId}
                          onChange={setPracticeId}
                          size="xs"
                          w={200}
                        />
                      )}
                      {offerAlsoText && (
                        <Checkbox
                          label="Also text the link"
                          description={alsoTextNote}
                          checked={alsoText}
                          disabled={!canAlsoText}
                          onChange={(event) => setAlsoText(event.currentTarget.checked)}
                          size="xs"
                          pb={6}
                        />
                      )}
                      <Button size="xs" onClick={sendLink} loading={sending} disabled={!readyToSend} mb={2}>
                        {needsPractice ? 'Send request' : 'Send link'}
                      </Button>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Discard this payment method"
                        onClick={discardDraft}
                        mb={6}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>

                    {/* Cherry's verdict on this patient, and the reason a send is
                      refused. Stated rather than left to a disabled button: a
                      rep who cannot see why the send is off has no way to know
                      the fix is to send the application first. */}
                    {needsPractice && cherryChecking && (
                      <Text size="xs" c="dimmed">
                        Checking Cherry…
                      </Text>
                    )}
                    {cherryNote && cherryNote.tone !== 'blocked' && (
                      <Text size="xs" c={CHERRY_TONE_COLORS[cherryNote.tone]}>
                        {cherryNote.label}
                      </Text>
                    )}
                    {cherryBlock && (
                      <Text size="xs" c="red">
                        {cherryBlock}
                      </Text>
                    )}
                  </Stack>
                )}

                {/* Edit-mode only, per the design: in read mode every figure on
                    the card is settled money, so there is nothing to reconcile.
                    While a draft exists it re-totals as the amount is typed,
                    which is what makes a split add up before it is sent. */}
                {choosing && (
                  <Group justify="space-between" pt={4} wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      Allocated{' '}
                      <Text component="span" size="xs" fw={600} c={fullyAllocated ? undefined : ORBIT_NEEDS_ACTION}>
                        {formatDollars(allocated)}
                      </Text>{' '}
                      of {formatDollars(order.amountTotal)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {methodCount === 1 ? '1 method' : `${methodCount} methods`}
                    </Text>
                  </Group>
                )}
              </Stack>
            </SimpleGrid>
          </Stack>
        )}

        <Divider
          labelPosition="center"
          label={
            <UnstyledButton
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              style={{ borderRadius: 999 }}
            >
              <Group
                gap={4}
                align="center"
                px="sm"
                py={4}
                style={{ border: '1px solid #e9ecef', borderRadius: 999, backgroundColor: '#ffffff' }}
              >
                <Text size="xs" c="dimmed" fw={500}>
                  {expanded ? 'Hide order detail' : 'Show order detail'}
                </Text>
                {expanded ? <IconChevronUp size={13} color="#868e96" /> : <IconChevronDown size={13} color="#868e96" />}
              </Group>
            </UnstyledButton>
          }
        />
      </Stack>
    </TimelineCard>
  );
}

/**
 * One payment leg.
 *
 * The actions are the whole point of the row: a live leg is **re-delivered**
 * (`Copy`, `Resend`) and a dead one is **replaced**. Nothing here routes through
 * the send path, so re-chasing a quiet patient cannot mint a second payable link
 * against the same balance — which is what happened when the only control was
 * "Resend link" at the top of the card.
 */
function MethodRow({
  leg,
  now,
  settledAt,
  link,
  canResend,
  canReplace,
  onCopy,
  onResend,
  onReplace,
}: {
  leg: TreatmentPaymentOrderPaymentIntent;
  now: Date;
  settledAt?: string;
  link?: string;
  canResend: boolean;
  canReplace: boolean;
  onCopy: (link: string) => Promise<void>;
  onResend: (link: string) => Promise<void>;
  onReplace: (leg: TreatmentPaymentOrderPaymentIntent) => void;
}) {
  const state = describeMethodState(leg, now, settledAt);
  const actions = methodActions(leg);
  const tone = state.tone === 'settled' ? ORBIT_SETTLED : ORBIT_NEEDS_ACTION;

  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <Text size="sm" fw={500} w={80} style={{ flexShrink: 0 }}>
        {formatProcessor(leg.processor) ?? leg.processor}
      </Text>
      <Text size="sm" w={90} ta="right" style={{ flexShrink: 0 }}>
        {formatDollars(leg.amount)}
      </Text>
      <Group gap={4} align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        {state.tone === 'settled' && leg.status === 'PAID' && <IconCheck size={14} color={tone} />}
        {(leg.status === 'FAILED' || leg.status === 'CANCELLED') && <IconCircleX size={14} color={tone} />}
        {state.tone === 'needs-action' && leg.status !== 'FAILED' && leg.status !== 'CANCELLED' && (
          <IconClock size={14} color={tone} />
        )}
        <Text size="sm" c={tone} truncate>
          {state.label}
        </Text>
      </Group>
      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {/* Copy and Resend need the link. Without a configured payment-frontend
            origin there is none, so the actions are absent rather than broken. */}
        {actions.includes('copy') && link && (
          <Anchor component="button" type="button" size="sm" onClick={() => void onCopy(link)}>
            Copy
          </Anchor>
        )}
        {actions.includes('resend') && link && canResend && (
          <Anchor component="button" type="button" size="sm" onClick={() => void onResend(link)}>
            Resend
          </Anchor>
        )}
        {actions.includes('replace') && canReplace && (
          <Anchor component="button" type="button" size="sm" onClick={() => onReplace(leg)}>
            Replace
          </Anchor>
        )}
      </Group>
    </Group>
  );
}
